import { getActiveParkingId } from "../../utils/tenant";
import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { 
    getFirestore, collection, query, onSnapshot, doc, updateDoc, setDoc, where 
} from "firebase/firestore";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    FiCheckCircle, FiXCircle, FiClock, FiFileText, FiUser, FiCalendar, FiShield
} from "react-icons/fi";

const MySwal = withReactContent(Swal);

export default function RequestManager() {
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // ⭐️ ШИНЭ: Харагдацын горим (Миний хүсэлтүүд эсвэл Super Admin бүх хүсэлт)
    const [viewMode, setViewMode] = useState("my_requests"); // 'my_requests' | 'all_requests'

    const db = getFirestore();
    const auth = getAuth();

    // ⭐️ ДАТА ТАТАХ (Горимоос хамаарч өөрчлөгдөнө)
    useEffect(() => {
        setIsLoading(true);
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (user) {
                const reqRef = collection(db, "parkings", getActiveParkingId(), "requests");
                let q;

                if (viewMode === "all_requests") {
                    // Super Admin горим: БҮХ хүсэлтийг татах
                    q = query(reqRef);
                } else {
                    // Энгийн горим: ЗӨВХӨН өөр рүү нь илгээсэн хүсэлтийг татах
                    q = query(reqRef, where("sentTo", "==", user.email));
                }
                
                const unsub = onSnapshot(q, (snapshot) => {
                    const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    // Саяхны хүсэлтүүдийг дээр гаргах
                    data.sort((a, b) => (b.createdAt?.toDate() || new Date()) - (a.createdAt?.toDate() || new Date()));
                    setRequests(data);
                    setIsLoading(false);
                });
                return () => unsub();
            } else {
                setIsLoading(false);
            }
        });
        return () => unsubscribeAuth();
    }, [db, auth, viewMode]); // viewMode өөрчлөгдөхөд дахин ажиллана

    const dateToString = (d) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    };

    // ХҮСЭЛТИЙГ БАТЛАХ / ТАТГАЛЗАХ ҮЙЛДЭЛ
    const handleAction = async (reqId, actionStatus, reqData) => {
        const actionText = actionStatus === 'approved' ? 'ЗӨВШӨӨРӨХ' : 'ТАТГАЛЗАХ';
        const actionColor = actionStatus === 'approved' ? '#10b981' : '#f43f5e';

        const result = await MySwal.fire({
            title: "Итгэлтэй байна уу?",
            text: `Та энэ хүсэлтийг ${actionText} гэж байна.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: actionColor,
            cancelButtonColor: "#94a3b8",
            confirmButtonText: `Тийм, ${actionText}`
        });

        if (result.isConfirmed) {
            setIsProcessing(true);
            try {
                // 1. Хэн баталгаажуулж байгааг гаргаж авах
                const currentUser = auth.currentUser;
                const approverEmail = currentUser ? currentUser.email : "Super Admin";

                // 2. Хүсэлтийн төлөвийг шинэчлэх
                await updateDoc(doc(db, "parkings", getActiveParkingId(), "requests", reqId), {
                    status: actionStatus,
                    approvedBy: approverEmail, 
                    updatedAt: new Date()
                });

                // 3. ЗӨВШӨӨРСӨН БОЛ: Ирцийн бааз руу автоматаар бүртгэх
                if (actionStatus === 'approved' && ['leave', 'annual_leave', 'business_trip', 'sick'].includes(reqData.type)) {
                    let currentDate = new Date(reqData.startDate);
                    const end = new Date(reqData.endDate);

                    while (currentDate <= end) {
                        const dStr = dateToString(currentDate);
                        const docId = `${dStr}_${reqData.emp_id}`;
                        
                        await setDoc(doc(db, "parkings", getActiveParkingId(), "daily_attendance", docId), {
                            emp_id: reqData.emp_id,
                            date: dStr,
                            status: reqData.type,
                            reason: reqData.reason,
                            updatedAt: new Date()
                        }, { merge: true });

                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                }

                MySwal.fire({ icon: 'success', title: 'Амжилттай', text: `Хүсэлт ${actionStatus === 'approved' ? 'баталгаажлаа' : 'цуцлагдлаа'}.`, timer: 2000, showConfirmButton: false });
            } catch (error) {
                MySwal.fire({ icon: 'error', title: 'Алдаа', text: 'Үйлдэл хийхэд алдаа гарлаа.' });
                console.error(error);
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const getRequestTypeText = (type) => {
        const types = { leave: "Чөлөө", annual_leave: "Амралт", business_trip: "Томилолт", sick: "Өвчтэй", overtime: "Илүү цаг" };
        return types[type] || type;
    };

    const pendingRequests = requests.filter(r => r.status === 'pending');
    const historyRequests = requests.filter(r => r.status !== 'pending');

    return (
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-4 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800">
            
            {/* Толгой хэсэг болон Super Admin шилжүүлэгч */}
            <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-start lg:tw-items-center tw-mb-8 tw-gap-4">
                <div>
                    <h2 className="tw-text-2xl tw-font-black tw-text-slate-900 tw-flex tw-items-center tw-gap-3">
                        <div className="tw-p-2 tw-bg-emerald-100 tw-text-emerald-600 tw-rounded-xl"><FiFileText size={24} /></div>
                        Хүсэлт Удирдлага
                    </h2>
                    <p className="tw-text-sm tw-text-slate-500 tw-mt-2 tw-ml-14">Ажилчдаас ирсэн чөлөө, илүү цагийн хүсэлтийг батлах хэсэг</p>
                </div>

                {/* ⭐️ SUPER ADMIN ШИЛЖҮҮЛЭГЧ ТОВЧ */}
                <div className="tw-flex tw-bg-slate-200 tw-p-1 tw-rounded-xl tw-shadow-inner">
                    <button 
                        onClick={() => setViewMode('my_requests')} 
                        className={`tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-rounded-lg tw-transition-all ${viewMode === 'my_requests' ? 'tw-bg-white tw-text-slate-800 tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-slate-700'}`}
                    >
                        Миний хариуцах
                    </button>
                    <button 
                        onClick={() => setViewMode('all_requests')} 
                        className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-rounded-lg tw-transition-all ${viewMode === 'all_requests' ? 'tw-bg-indigo-600 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-slate-700'}`}
                    >
                        <FiShield /> Super Admin (Бүгд)
                    </button>
                </div>
            </div>

            {/* ХҮЛЭЭГДЭЖ БУЙ ХҮСЭЛТҮҮД */}
            <div className="tw-mb-8">
                <h3 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-4 tw-flex tw-items-center tw-gap-2">
                    <FiClock className="tw-text-amber-500"/> Шийдвэр хүлээгдэж буй ({pendingRequests.length})
                </h3>
                
                {isLoading ? <div className="tw-text-center tw-text-slate-400">Уншиж байна...</div> : pendingRequests.length === 0 ? (
                    <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-10 tw-text-center tw-text-slate-400">
                        {viewMode === 'all_requests' ? "Байгууллагын хэмжээнд шинэ хүсэлт алга байна." : "Танд ирсэн шинэ хүсэлт алга байна."}
                    </div>
                ) : (
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                        {pendingRequests.map(req => (
                            <div key={req.id} className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-flex tw-flex-col">
                                <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-bg-slate-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-slate-600"><FiUser/></div>
                                        <div>
                                            <h4 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0">{req.emp_name}</h4>
                                            <span className="tw-text-[10px] tw-text-slate-500">{req.department}</span>
                                        </div>
                                    </div>
                                    <span className="tw-bg-amber-100 tw-text-amber-700 tw-px-2 tw-py-1 tw-rounded-md tw-text-[10px] tw-font-bold">{getRequestTypeText(req.type)}</span>
                                </div>
                                
                                <div className="tw-bg-slate-50 tw-p-3 tw-rounded-xl tw-mb-4 tw-flex-1">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-bold tw-text-slate-700 tw-mb-2">
                                        <FiCalendar className="tw-text-blue-500"/> 
                                        {req.type === 'overtime' ? `${req.startDate} (${req.startTime} - ${req.endTime})` : `${req.startDate} — ${req.endDate}`}
                                    </div>
                                    <p className="tw-text-xs tw-text-slate-600 tw-m-0 tw-leading-relaxed">"{req.reason}"</p>
                                    
                                    {/* ⭐️ Super Admin горимд үед хэнд илгээснийг харуулах */}
                                    {viewMode === 'all_requests' && (
                                        <div className="tw-mt-3 tw-pt-2 tw-border-t tw-border-slate-200 tw-text-[10px] tw-font-bold tw-text-indigo-500">
                                            Илгээсэн удирдлага: {req.sentTo}
                                        </div>
                                    )}
                                </div>

                                <div className="tw-flex tw-gap-2">
                                    <button onClick={() => handleAction(req.id, 'approved', req)} disabled={isProcessing} className="tw-flex-1 tw-py-2 tw-bg-emerald-50 tw-text-emerald-700 tw-font-bold tw-text-sm tw-rounded-xl hover:tw-bg-emerald-500 hover:tw-text-white tw-transition-colors tw-border tw-border-emerald-200">
                                        Зөвшөөрөх
                                    </button>
                                    <button onClick={() => handleAction(req.id, 'rejected', req)} disabled={isProcessing} className="tw-flex-1 tw-py-2 tw-bg-rose-50 tw-text-rose-700 tw-font-bold tw-text-sm tw-rounded-xl hover:tw-bg-rose-500 hover:tw-text-white tw-transition-colors tw-border tw-border-rose-200">
                                        Татгалзах
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ШИЙДВЭРЛЭГДСЭН ТҮҮХ */}
            <div>
                <h3 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-4">
                    {viewMode === 'all_requests' ? "Байгууллагын шийдвэрлэгдсэн түүх" : "Миний шийдвэрлэсэн түүх"}
                </h3>
                <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-overflow-hidden">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-left tw-text-sm min-w-[700px]">
                            <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-100">
                                <tr>
                                    <th className="tw-px-6 tw-py-4 tw-font-bold tw-text-slate-500">Ажилтан</th>
                                    <th className="tw-px-6 tw-py-4 tw-font-bold tw-text-slate-500">Төрөл / Огноо</th>
                                    <th className="tw-px-6 tw-py-4 tw-font-bold tw-text-slate-500">Төлөв</th>
                                    <th className="tw-px-6 tw-py-4 tw-font-bold tw-text-slate-500">Баталсан хүн</th>
                                </tr>
                            </thead>
                            <tbody className="tw-divide-y tw-divide-slate-100">
                                {historyRequests.length === 0 ? <tr><td colSpan={4} className="tw-text-center tw-py-8 tw-text-slate-400">Түүх байхгүй байна.</td></tr> : 
                                historyRequests.slice(0, 50).map(req => (
                                    <tr key={req.id} className="hover:tw-bg-slate-50">
                                        <td className="tw-px-6 tw-py-4">
                                            <div className="tw-font-bold tw-text-slate-800">{req.emp_name}</div>
                                            <div className="tw-text-[10px] tw-text-slate-400">{req.department}</div>
                                        </td>
                                        <td className="tw-px-6 tw-py-4">
                                            <div className="tw-font-bold tw-text-slate-700">{getRequestTypeText(req.type)}</div>
                                            <div className="tw-text-xs tw-text-blue-600">
                                                {req.type === 'overtime' ? `${req.startDate} (${req.startTime} - ${req.endTime})` : `${req.startDate} — ${req.endDate}`}
                                            </div>
                                        </td>
                                        <td className="tw-px-6 tw-py-4">
                                            {req.status === 'approved' ? (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-emerald-100 tw-text-emerald-700 tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-bold"><FiCheckCircle/> Зөвшөөрсөн</span>
                                            ) : (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-rose-100 tw-text-rose-700 tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-bold"><FiXCircle/> Татгалзсан</span>
                                            )}
                                        </td>
                                        <td className="tw-px-6 tw-py-4">
                                            <div className="tw-text-xs tw-font-bold tw-text-slate-700">{req.approvedBy || "Тодорхойгүй"}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </div>
    );
}